/**
 * Canonical Amazon affiliate primitives — the single source of truth for the
 * Associates tag and the product-link format. Runtime code (app/, components/,
 * lib/, data/) must import from here instead of hardcoding either one; a guard
 * test (__tests__/lib/affiliate.test.ts) enforces this. Historical
 * one-off scripts under scripts/ are exempt by design.
 *
 * Field ownership: ASIN is the shared key across every product data source.
 * PAAPI is the authority for current price and product title (synced by the
 * weekly paapi:refresh workflow). Static data files and the Directus
 * `products` collection own editorial copy (descriptions, badges, reasons).
 * A price or title in a data file is a cache of PAAPI — never hand-edit one.
 */

export const AMAZON_AFFILIATE_TAG = 'whatgrassis0b-20'

/**
 * Surface families that can carry their own Associates tracking ID, so Amazon's
 * reports split earnings by surface. Surfaces outside these families (compare,
 * result picks, diagnosis, catalog, footer, ...) always use the site tag.
 */
export type AmazonTagFamily = 'card' | 'inline' | 'tool' | 'rail'

export const AMAZON_TAG_FAMILIES: readonly AmazonTagFamily[] = ['card', 'inline', 'tool', 'rail']

/** Associates tracking-ID shape: store id + `-NN`. */
const TRACKING_ID_SHAPE = /^[a-z0-9][a-z0-9-]*-\d{2}$/i

/**
 * Each read is a literal `process.env.NEXT_PUBLIC_*` access so Next.js inlines
 * the value into client bundles; a computed key would read undefined in the
 * browser. Values are read per call so tests can set them.
 */
function configuredFamilyTag(family: AmazonTagFamily): string | undefined {
  switch (family) {
    case 'card':
      return process.env.NEXT_PUBLIC_AMAZON_TAG_CARD
    case 'inline':
      return process.env.NEXT_PUBLIC_AMAZON_TAG_INLINE
    case 'tool':
      return process.env.NEXT_PUBLIC_AMAZON_TAG_TOOL
    case 'rail':
      return process.env.NEXT_PUBLIC_AMAZON_TAG_RAIL
  }
}

/**
 * Tracking ID for a surface family. Falls back to the site tag when the env
 * var is unset, blank, or not tracking-ID shaped, so an unconfigured or
 * mistyped ID never ships an untagged link.
 */
export function amazonTagFor(family?: AmazonTagFamily | null): string {
  if (!family) return AMAZON_AFFILIATE_TAG
  const configured = configuredFamilyTag(family)?.trim()
  return configured && TRACKING_ID_SHAPE.test(configured) ? configured : AMAZON_AFFILIATE_TAG
}

/** Every tag runtime code may emit: the site tag plus any configured family tags. */
export function allowedAmazonTags(): Set<string> {
  const tags = new Set([AMAZON_AFFILIATE_TAG])
  for (const family of AMAZON_TAG_FAMILIES) tags.add(amazonTagFor(family))
  return tags
}

/**
 * Placement/surface key (the `data-affiliate-placement` or analytics `surface`
 * value) to tag family. Unlisted keys return null and keep the site tag.
 */
const PLACEMENT_TAG_FAMILY: Record<string, AmazonTagFamily> = {
  // Body cards
  'card-v1': 'card',
  'blog-card-v1': 'card',
  callout: 'card',
  'blog-callout': 'card',
  'callout-early': 'card',
  'blog-callout-early': 'card',
  'editor-pick': 'card',
  'blog-editor-pick': 'card',
  'verdict-card': 'card',
  'blog-verdict-card': 'card',
  'comparison-table': 'card',
  'guide-early': 'card',
  'guide-section': 'card',
  'guide-care-task': 'card',
  // Inline text links
  'in-content': 'inline',
  inline: 'inline',
  'blog-inline-anchor': 'inline',
  // Rails
  sidebar: 'rail',
  'guide-sidebar': 'rail',
  'guide-sidebar-inline': 'rail',
  'blog-sidebar': 'rail',
  'blog-picks-rail': 'rail',
  'blog-rail-mobile': 'rail',
}

export function amazonTagFamilyForPlacement(placement: string | null | undefined): AmazonTagFamily | null {
  if (!placement) return null
  const direct = PLACEMENT_TAG_FAMILY[placement]
  if (direct) return direct
  if (placement.startsWith('tool-') || placement.startsWith('tool_')) return 'tool'
  if (placement.startsWith('guide-sidebar') || placement.startsWith('blog-sidebar')) return 'rail'
  return null
}

/** B-prefixed ASIN or legacy 10-digit ISBN. */
const ASIN_SHAPE = /^(?:B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/

export function isValidAsin(asin: string): boolean {
  return ASIN_SHAPE.test(asin)
}

/**
 * Canonical affiliate product URL. Throws on a malformed ASIN so a typo in a
 * data file fails the build instead of shipping a dead $0 link.
 */
export function amazonUrl(asin: string, family?: AmazonTagFamily | null): string {
  if (!ASIN_SHAPE.test(asin)) {
    throw new Error(`amazonUrl: "${asin}" is not a valid ASIN`)
  }
  return `https://www.amazon.com/dp/${asin}/ref=nosim?tag=${amazonTagFor(family)}`
}

/** Tagged Amazon search URL — for products without a stable single ASIN. */
export function amazonSearchUrl(query: string, family?: AmazonTagFamily | null): string {
  return `https://www.amazon.com/s?k=${encodeURIComponent(query)}&tag=${amazonTagFor(family)}`
}

const AMAZON_HOST = /^(?:www\.|smile\.)?amazon\.com$/i
const SITE_TAG_PARAM = new RegExp(`([?&])tag=${AMAZON_AFFILIATE_TAG}(?=&|#|$)`)

/**
 * Re-tag an already-built Amazon URL for a surface family. Only a URL that
 * carries the site tag is rewritten: other tags (the weed-ID tracking ID,
 * already-retagged links) and non-Amazon URLs pass through byte-for-byte, as
 * does everything when the family's env var is unset.
 */
export function withAmazonTag(url: string, family: AmazonTagFamily | null | undefined): string {
  if (!url || !family) return url
  const tag = amazonTagFor(family)
  if (tag === AMAZON_AFFILIATE_TAG) return url
  let host: string
  try {
    host = new URL(url).hostname
  } catch {
    return url
  }
  if (!AMAZON_HOST.test(host)) return url
  return url.replace(SITE_TAG_PARAM, `$1tag=${tag}`)
}

/** `withAmazonTag` keyed by placement/surface; unmapped placements keep the site tag. */
export function withAmazonTagForPlacement(url: string, placement: string | null | undefined): string {
  return withAmazonTag(url, amazonTagFamilyForPlacement(placement))
}
